import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
} from '@mui/material';
import {
  TrendingUp,
  People,
  Business,
  Work,
  School,
  MoreVert,
  Notifications,
  Assessment,
  Group,
  Timeline,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { useSelector } from 'react-redux';
import { useGetDashboardStatsQuery, useGetMyDashboardQuery } from '../../../services/dashboardApi';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const AdminDashboard = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { isAuthenticated } = useSelector((state) => state.auth);
  const { data: dashboardStats, isLoading } = useGetDashboardStatsQuery(undefined, {
    skip: !isAuthenticated,
  });
  const { data: userData } = useGetMyDashboardQuery(undefined, {
    skip: !isAuthenticated,
  });

  const user = userData?.profile || {};
  const adminName = `${user.firstName || 'Administrator'}`.trim();
  const adminFullName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Administrator';

  const [dashboardData, setDashboardData] = useState({
    totalEmployees: 0,
    activeEmployees: 0,
    totalDepartments: 0,
    totalDesignations: 0,
    maleEmployees: 0,
    femaleEmployees: 0,
    newHires: 0,
    attendanceRate: 0,
    productivityScore: 0,
  });

  const [employeeGrowthData, setEmployeeGrowthData] = useState([]);
  const [departmentData, setDepartmentData] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([
    {
      id: 1,
      title: 'Team Meeting',
      date: 'Oct 15, 2024',
      time: '10:00 AM',
      type: 'meeting',
    },
    {
      id: 2,
      title: 'Performance Review',
      date: 'Oct 18, 2024',
      time: '2:00 PM',
      type: 'review',
    },
    {
      id: 3,
      title: 'Training Session',
      date: 'Oct 22, 2024',
      time: '3:00 PM',
      type: 'training',
    },
  ]);

  useEffect(() => {
    if (dashboardStats) {
      setDashboardData({
        ...dashboardData,
        ...dashboardStats.stats,
        maleEmployees: dashboardStats.genderDistribution.male || 0,
        femaleEmployees: dashboardStats.genderDistribution.female || 0,
      });
      setEmployeeGrowthData(dashboardStats.employeeGrowth || []);
      setDepartmentData(dashboardStats.departmentDistribution || []);
      setRecentActivities(dashboardStats.recentActivities || []);
    }
  }, [dashboardStats]);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Chart.js data configurations
  const employeeGrowthChartData = {
    labels: employeeGrowthData.map(item => item.month),
    datasets: [
      {
        label: 'Employees',
        data: employeeGrowthData.map(item => item.employees),
        borderColor: '#7522D1',
        backgroundColor: 'rgba(117, 34, 209, 0.1)',
        borderWidth: 3,
        pointBackgroundColor: '#7522D1',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 6,
        tension: 0.1,
      },
    ],
  };

  const genderDistributionChartData = {
    labels: ['Male', 'Female'],
    datasets: [
      {
        data: [dashboardData.maleEmployees, dashboardData.femaleEmployees],
        backgroundColor: ['#7522D1', '#E91E63'],
        borderColor: ['#fff', '#fff'],
        borderWidth: 2,
      },
    ],
  };

  const departmentDistributionChartData = {
    labels: departmentData.map(item => item.name),
    datasets: [
      {
        label: 'Employees',
        data: departmentData.map(item => item.value),
        backgroundColor: '#7522D1',
        borderColor: '#7522D1',
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          color: '#666',
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#666',
        },
      },
    },
  };

  const StatCard = ({ title, value, subtitle, icon, color, trend }) => (
    <Card
      sx={{
        height: '100%',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          width: '100px',
          height: '100px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '50%',
          transform: 'translate(30px, -30px)',
        },
      }}
    >
      <CardContent sx={{ p: 2, position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ fontSize: '0.9rem', fontWeight: 500, opacity: 0.9 }}>
              {title}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.8rem', opacity: 0.8 }}>
              {subtitle}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              px: 1,
              py: 0.5,
              borderRadius: 1,
            }}
          >
            {trend === 'up' ? (
              <ArrowUpward sx={{ fontSize: 16, color: '#4CAF50' }} />
            ) : (
              <ArrowDownward sx={{ fontSize: 16, color: '#FF5252' }} />
            )}
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
              {trend === 'up' ? '+12%' : '-5%'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <Box sx={{ fontSize: '2rem', fontWeight: 700 }}>{value}</Box>
          <Box sx={{ fontSize: '0.8rem', opacity: 0.8 }}>{icon}</Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <CircularProgress sx={{ color: 'white' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          p: 3,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={user.profileImage || ""}
              sx={{ bgcolor: 'white', color: '#7522D1' }}
            >
              {!user.profileImage && adminName.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Admin Dashboard
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Welcome back, {adminFullName}
              </Typography>
            </Box>
          </Box>

        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ p: 3 }}>
        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="Total Employees"
              value={dashboardData.totalEmployees}
              subtitle="Active workforce"
              icon={<People />}
              color="#7522D1"
              trend="up"
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="Active Employees"
              value={dashboardData.activeEmployees}
              subtitle="Currently working"
              icon={<Work />}
              color="#4CAF50"
              trend="up"
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="Departments"
              value={dashboardData.totalDepartments}
              subtitle="Total departments"
              icon={<Business />}
              color="#FF9800"
              trend="up"
            />
          </Grid>
          <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              title="Designations"
              value={dashboardData.totalDesignations}
              subtitle="Job roles"
              icon={<School />}
              color="#2196F3"
              trend="up"
            />
          </Grid>
        </Grid>

        {/* Charts and Activity Section */}
        <Grid container spacing={3}>
          {/* Employee Growth Chart */}
          <Grid item size={{ xs: 12, md: 8 }}>
            <Card sx={{ height: 400, p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Employee Growth
                </Typography>
                <Chip
                  label="Last 6 months"
                  size="small"
                  sx={{ backgroundColor: '#7522D1', color: 'white' }}
                />
              </Box>
              <Box sx={{ height: 300 }}>
                <Line data={employeeGrowthChartData} options={chartOptions} />
              </Box>
            </Card>
          </Grid>

          {/* Gender Distribution */}
          <Grid item size={{ xs: 12, md: 4 }}>
            <Card sx={{ height: 400, p: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Gender Distribution
              </Typography>
              <Box sx={{ height: 300 }}>
                <Doughnut
                  data={genderDistributionChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom',
                        labels: {
                          color: '#666',
                          padding: 20,
                        },
                      },
                    },
                    cutout: '60%',
                  }}
                />
              </Box>
            </Card>
          </Grid>

          {/* Department Distribution */}
          <Grid item size={{ xs: 12, md: 6 }}>
            <Card sx={{ height: 350, p: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Department Distribution
              </Typography>
              <Box sx={{ height: 280 }}>
                <Bar data={departmentDistributionChartData} options={chartOptions} />
              </Box>
            </Card>
          </Grid>

          {/* Recent Activities */}
          <Grid item size={{ xs: 12, md: 6 }}>
            <Card sx={{ height: 350, p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Recent Activities
                </Typography>
                <Button size="small" sx={{ color: '#7522D1' }}>
                  View All
                </Button>
              </Box>
              <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
                <List>
                  {recentActivities.map((activity) => (
                    <ListItem key={activity.id} sx={{ px: 0, py: 1 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            backgroundColor: activity.color || '#7522D1',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {activity.icon || <Assessment />}
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={activity.title}
                        primaryTypographyProps={{ 
                          variant: 'body2', 
                          sx: { fontWeight: 500 },
                          component: 'div' 
                        }}
                        secondary={
                          <Box component="div">
                            <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                              {activity.subtitle}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
                              {new Date(activity.time).toLocaleString()}
                            </Typography>
                          </Box>
                        }
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* Upcoming Events */}
        <Grid item size={{ xs: 12 }}>
          <Card sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Upcoming Events
            </Typography>
            <Grid container spacing={2}>
              {upcomingEvents.map((event) => (
                <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={event.id}>
                  <Paper
                    sx={{
                      p: 2,
                      borderLeft: `4px solid ${event.type === 'meeting' ? '#4CAF50' :
                        event.type === 'review' ? '#FF9800' : '#2196F3'
                        }`,
                      backgroundColor: '#f8f9fa',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      {event.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                      {event.date}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#999' }}>
                      {event.time}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Card>
        </Grid>
      </Box>
    </Box>
  );
};

export default AdminDashboard;